import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { dataSourcesService, DataSource } from '../../services/dataSourcesService'
import { functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'
import TestConnectionDialog from './TestConnectionDialog'
import { 
  Database, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  Info,
  Key,
  Shield,
  Globe,
  Loader2
} from 'lucide-react'

interface FirestoreConnection {
  connectionName: string
  projectId: string
  serviceAccountKey: string
}

interface FirestoreConnectFormProps {
  onBack: () => void
  onConnect: (connection: FirestoreConnection) => void
  organizationId: string
  editingDataSource?: DataSource | null
}

const FirestoreConnectForm: React.FC<FirestoreConnectFormProps> = ({ 
  onBack, 
  onConnect, 
  organizationId, 
  editingDataSource 
}) => {
  const isEditMode = !!editingDataSource
  
  const [connectionName, setConnectionName] = useState('')
  const [projectId, setProjectId] = useState('')
  const [serviceAccountKey, setServiceAccountKey] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [validationStatus, setValidationStatus] = useState<'idle' | 'valid' | 'invalid'>('idle')
  const [isTesting, setIsTesting] = useState(false)
  const { toast } = useToast()

  // Populate form when editing
  useEffect(() => {
    if (editingDataSource && editingDataSource.type === 'firestore') {
      setConnectionName(editingDataSource.name)
      const config = editingDataSource.configuration as any
      if (config?.projectId) {
        setProjectId(config.projectId)
      }
      // For edit mode, we don't pre-populate the service account key for security
      // User needs to provide it again if they want to update it
    }
  }, [editingDataSource])

  const validateServiceAccountKey = (keyContent: string): boolean => {
    try {
      const parsed = JSON.parse(keyContent)
      return (
        parsed.type === 'service_account' &&
        parsed.project_id &&
        parsed.private_key_id &&
        parsed.private_key &&
        parsed.client_email &&
        parsed.client_id
      )
    } catch {
      return false
    }
  }

  const handleServiceAccountKeyChange = (value: string) => {
    setServiceAccountKey(value)
    if (value.trim()) {
      const isValid = validateServiceAccountKey(value)
      setValidationStatus(isValid ? 'valid' : 'invalid')
      
      // Auto-fill project ID if valid
      if (isValid) {
        try {
          const parsed = JSON.parse(value)
          if (parsed.project_id && !projectId) {
            setProjectId(parsed.project_id)
          }
        } catch {
          // Ignore parse errors
        }
      }
    } else {
      setValidationStatus('idle')
    }
  }

  const handleTestConnection = async () => {
    // For edit mode with existing data source, test the existing connection
    if (isEditMode && editingDataSource && !serviceAccountKey.trim()) {
      setIsValidating(true)
      setIsTesting(true)
      
      try {
        const testDataSourceConnection = httpsCallable(functions, 'testDataSourceConnection')
        const response = await testDataSourceConnection({
          dataSourceId: editingDataSource.id
        })

        const result = response.data as any

        if (result.success) {
          toast({
            title: "Connection Test Successful",
            description: "Successfully connected to your Firestore database.",
            variant: "success",
          })
        } else {
          throw new Error(result.message || result.error || 'Connection test failed')
        }
        
      } catch (error: any) {
        console.error('Error testing Firestore connection:', error)
        toast({
          variant: "destructive",
          title: "Connection Test Failed",
          description: error.message || "Failed to test connection to Firestore database.",
        })
      } finally {
        setIsValidating(false)
        setIsTesting(false)
      }
      return
    }

    // For new connections or updating service account key
    if (!connectionName.trim() || !projectId.trim() || !serviceAccountKey.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields to test the connection.",
      })
      return
    }

    if (validationStatus !== 'valid') {
      toast({
        variant: "destructive",
        title: "Invalid Service Account",
        description: "Please provide a valid service account JSON key.",
      })
      return
    }

    setIsValidating(true)
    setIsTesting(true)
    
    try {
      // Create a temporary data source to test the connection
      const createDataSource = httpsCallable(functions, 'createDataSource')
      
      const response = await createDataSource({
        name: `temp-test-${Date.now()}`, // Temporary name for testing
        type: 'firestore',
        organizationId: organizationId,
        configuration: {
          authType: 'service-account-key',
          projectId: projectId,
          serviceAccountKey: serviceAccountKey,
        },
        description: 'Temporary test connection',
        visibility: 'organization',
      })

      const result = response.data as any

      if (result.success) {
        // Connection test was successful, now clean up the temporary data source
        const deleteDataSource = httpsCallable(functions, 'deleteDataSource')
        await deleteDataSource({ dataSourceId: result.dataSourceId })

        toast({
          title: "Connection Test Successful",
          description: "Successfully connected to your Firestore database.",
          variant: "success",
        })
      } else {
        throw new Error(result.message || 'Connection test failed')
      }
      
    } catch (error: any) {
      console.error('Error testing Firestore connection:', error)
      toast({
        variant: "destructive",
        title: "Connection Test Failed",
        description: error.message || "Failed to test connection to Firestore database.",
      })
    } finally {
      setIsValidating(false)
      setIsTesting(false)
    }
  }

  const updateFirestoreDataSource = async () => {
    if (!editingDataSource) return;
    
    try {
      setIsConnecting(true)
      
      const updateDataSource = httpsCallable(functions, 'updateDataSource')
      
      // For updates, we build the configuration object conditionally
      const configurationUpdate: any = {
        authType: 'service-account-key',
        projectId: projectId,
      }
      
      // Only include serviceAccountKey if user provided a new one
      if (serviceAccountKey.trim() && validationStatus === 'valid') {
        configurationUpdate.serviceAccountKey = serviceAccountKey
      }
      
      const response = await updateDataSource({
        dataSourceId: editingDataSource.id,
        name: connectionName,
        configuration: configurationUpdate,
        description: `Firestore database for project ${projectId}`,
      })

      const result = response.data as any

      if (result.success) {
        toast({
          title: "Firestore Connection Updated",
          description: `Successfully updated connection "${connectionName}".`,
          variant: "success",
        })
        
        onConnect({
          connectionName,
          projectId,
          serviceAccountKey
        })
      } else {
        throw new Error(result.message || 'Failed to update Firestore data source')
      }
      
    } catch (error: any) {
      console.error('Error updating Firestore data source:', error)
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update Firestore data source.",
        variant: "destructive",
      })
    } finally {
      setIsConnecting(false)
    }
  }

  const handleConnect = async () => {
    if (!connectionName.trim() || !projectId.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields.",
      })
      return
    }

    // For edit mode, service account key is optional (only if user wants to update it)
    if (!isEditMode && !serviceAccountKey.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please provide a service account JSON key.",
      })
      return
    }

    // If service account key is provided, it must be valid
    if (serviceAccountKey.trim() && validationStatus !== 'valid') {
      toast({
        variant: "destructive",
        title: "Invalid Service Account",
        description: "Please provide a valid service account JSON key.",
      })
      return
    }

    if (isEditMode) {
      await updateFirestoreDataSource()
      return
    }

    setIsConnecting(true)
    
    try {
      const result = await dataSourcesService.createFirestoreDataSource(
        connectionName,
        projectId,
        serviceAccountKey,
        organizationId
      )

      toast({
        title: "Data Source Created",
        description: result.message,
        variant: "success",
      })

      // Call the parent's onConnect handler with the connection info
      onConnect({
        connectionName,
        projectId,
        serviceAccountKey
      })

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Connection Failed",
        description: error.message || "Failed to create data source connection.",
      })
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Data Sources
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Database className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight text-foreground">
            {isEditMode ? `Edit ${editingDataSource?.name}` : 'Connect to Firestore'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditMode 
              ? 'Update your Firestore database connection settings' 
              : 'Set up a secure connection to your Firebase/Firestore database'
            }
          </p>
        </div>
      </div>

      {/* Security Notice */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          Your service account credentials will be encrypted and securely stored. FireForge will only access 
          data you explicitly grant permissions for.
        </AlertDescription>
      </Alert>

      {/* Connection Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Connection Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Name */}
          <div className="space-y-2">
            <Label htmlFor="connection-name">Connection Name *</Label>
            <Input
              id="connection-name"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="e.g., My App's User Data"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              A friendly name to identify this connection in your dashboard
            </p>
          </div>

          {/* Project ID */}
          <div className="space-y-2">
            <Label htmlFor="project-id">Firebase Project ID *</Label>
            <Input
              id="project-id"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="your-firebase-project-id"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              The project ID from your Firebase console URL
            </p>
          </div>

          {/* Service Account Key */}
          <div className="space-y-2">
            <Label htmlFor="service-account" className="flex items-center gap-2">
              Service Account JSON Key {!isEditMode && '*'}
              {isEditMode && ' (Optional)'}
              {validationStatus === 'valid' && <CheckCircle className="h-4 w-4 text-green-500" />}
              {validationStatus === 'invalid' && <AlertCircle className="h-4 w-4 text-red-500" />}
            </Label>
            <Textarea
              id="service-account"
              value={serviceAccountKey}
              onChange={(e) => handleServiceAccountKeyChange(e.target.value)}
              placeholder={isEditMode 
                ? 'Leave empty to keep existing key, or paste a new service account JSON key to update it.'
                : 'Paste your service account JSON key here. Example: { "type": "service_account", ... }'
              }
              className="min-h-[200px] font-mono text-sm"
            />
            {isEditMode && !serviceAccountKey.trim() && (
              <div className="flex items-center gap-2 text-blue-600 text-sm">
                <Info className="h-4 w-4" />
                Your existing service account key will be preserved
              </div>
            )}
            {validationStatus === 'valid' && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                Valid service account key detected
              </div>
            )}
            {validationStatus === 'invalid' && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                Invalid JSON or missing required fields
              </div>
            )}
          </div>

          {/* Test Connection Button */}
          <div className="pt-4">
            <Button 
              variant="outline" 
              onClick={handleTestConnection}
              disabled={
                (!isEditMode && validationStatus !== 'valid') || 
                (isEditMode && serviceAccountKey.trim() && validationStatus !== 'valid') ||
                isValidating
              }
              className="w-full"
            >
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            How to get a Service Account Key
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium">Step 1: Access Google Cloud Console</h4>
              <p className="text-muted-foreground">
                Go to your Firebase project in the <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Cloud Console</a>
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Step 2: Navigate to Service Accounts</h4>
              <p className="text-muted-foreground">
                Go to IAM & Admin → Service Accounts in the left sidebar
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Step 3: Create or Select Service Account</h4>
              <p className="text-muted-foreground">
                Select an existing service account or create a new one with Firestore read access 
                (e.g., "Cloud Datastore User" role)
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Step 4: Generate Key</h4>
              <p className="text-muted-foreground">
                Click on the service account → Keys tab → Add Key → Create new key → Choose JSON format
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Step 5: Copy the JSON</h4>
              <p className="text-muted-foreground">
                Open the downloaded JSON file and paste its entire content in the field above
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={handleConnect} 
          disabled={
            (!isEditMode && validationStatus !== 'valid') || 
            (isEditMode && serviceAccountKey.trim() && validationStatus !== 'valid') ||
            isConnecting
          }
          className="flex-1"
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {isEditMode ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              {isEditMode ? 'Update Connection' : 'Connect & Save'}
            </>
          )}
        </Button>
      </div>

      {/* Testing Connection Dialog */}
      <TestConnectionDialog 
        isOpen={isTesting}
        dataSourceName={connectionName || 'Firestore Database'}
      />
    </div>
  )
}

export default FirestoreConnectForm 